// src/services/storage.service.ts
/**
 * StorageService - IndexedDB wrapper for persistent vault storage
 *
 * Provides CRUD operations, search, export/import, and statistics
 * for vault items. Uses IndexedDB for reliable client-side storage.
 */

import { useState, useEffect, useCallback } from 'react';
import type { VaultItem, EncryptedData, HashResult } from './crypto.service';

// Re-export types for convenience
export type { VaultItem, EncryptedData, HashResult };

// Storage configuration
const DB_NAME = 'AventHorizonVault';
const DB_VERSION = 1;
const STORE_NAME = 'vaults';
const EXPORT_FORMAT_VERSION = '2.0.0'; // Bumped for 600k PBKDF2 iterations

// Storage statistics interface
export interface StorageStats {
  totalVaults: number;
  totalSize: number;
  oldestVault: string | null;
  newestVault: string | null;
}

// Export data format
export interface ExportData {
  /** Export format version for migration compatibility */
  version: string;
  /** ISO timestamp of when the export was created */
  exportedAt: string;
  /** Array of vault items */
  vaults: VaultItem[];
  /** Crypto parameters used (for migration) */
  cryptoParams?: {
    pbkdf2Iterations: number;
    algorithm: string;
  };
}

// Import result
export interface ImportResult {
  imported: number;
  skipped: number;
  errors: string[];
}

export class StorageService {
  private db: IDBDatabase | null = null;
  private dbReady: Promise<IDBDatabase>;
  private resolveDbReady!: (db: IDBDatabase) => void;
  private rejectDbReady!: (error: Error) => void;

  constructor() {
    this.dbReady = new Promise((resolve, reject) => {
      this.resolveDbReady = resolve;
      this.rejectDbReady = reject;
    });
  }

  /**
   * Initialize the database connection
   * Must be called before any other operations
   */
  async init(): Promise<void> {
    if (this.db) {
      return;
    }

    return new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        const error = new Error('IndexedDB is not supported in this environment');
        this.rejectDbReady(error);
        reject(error);
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        const error = new Error(`Failed to open database: ${request.error?.message || 'Unknown error'}`);
        this.rejectDbReady(error);
        reject(error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.resolveDbReady(this.db);
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        this.createSchema(db);
      };
    });
  }

  /**
   * Create the database schema with indexes
   */
  private createSchema(db: IDBDatabase): void {
    // Create the vaults object store with id as key path
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });

      // Create indexes for efficient queries
      store.createIndex('createdAt', 'createdAt', { unique: false });
      store.createIndex('expiresAt', 'expiresAt', { unique: false });
      store.createIndex('name', 'name', { unique: false });
      store.createIndex('hash', 'hash.hash', { unique: false });
    }
  }

  /**
   * Get the database instance, waiting for initialization if needed
   */
  private async getDb(): Promise<IDBDatabase> {
    if (this.db) {
      return this.db;
    }
    return this.dbReady;
  }

  /**
   * Execute a transaction and return a promise
   */
  private async withTransaction<T>(
    mode: IDBTransactionMode,
    callback: (store: IDBObjectStore) => IDBRequest<T>
  ): Promise<T> {
    const db = await this.getDb();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, mode);
      const store = transaction.objectStore(STORE_NAME);

      const request = callback(store);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(new Error(`Transaction failed: ${request.error?.message || 'Unknown error'}`));
    });
  }

  /**
   * Execute a cursor-based query
   */
  private async withCursor<T>(
    indexName: string | null,
    direction: IDBCursorDirection,
    callback: (cursor: IDBCursorWithValue, results: T[]) => boolean // Return false to stop iteration
  ): Promise<T[]> {
    const db = await this.getDb();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const source = indexName ? store.index(indexName) : store;
      const results: T[] = [];

      const request = source.openCursor(null, direction);

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const shouldContinue = callback(cursor, results);
          if (shouldContinue) {
            cursor.continue();
          } else {
            resolve(results);
          }
        } else {
          resolve(results);
        }
      };

      request.onerror = () => reject(new Error(`Cursor query failed: ${request.error?.message || 'Unknown error'}`));
    });
  }

  // ==================== CRUD Operations ====================

  /**
   * Save a vault item to storage
   */
  async saveVault(vault: VaultItem): Promise<VaultItem> {
    await this.withTransaction('readwrite', (store) => store.put(vault));
    return vault;
  }

  /**
   * Get a vault by ID
   */
  async getVault(id: string): Promise<VaultItem | null> {
    const result = await this.withTransaction<VaultItem | undefined>('readonly', (store) => store.get(id));
    return result || null;
  }

  /**
   * Get all vaults
   */
  async getAllVaults(): Promise<VaultItem[]> {
    const result = await this.withTransaction<VaultItem[]>('readonly', (store) => store.getAll());
    return result || [];
  }

  /**
   * Delete a vault by ID
   */
  async deleteVault(id: string): Promise<boolean> {
    const exists = await this.vaultExists(id);
    if (!exists) {
      return false;
    }
    await this.withTransaction('readwrite', (store) => store.delete(id));
    return true;
  }

  /**
   * Delete all vaults
   */
  async deleteAllVaults(): Promise<number> {
    const count = (await this.getAllVaults()).length;
    await this.withTransaction('readwrite', (store) => store.clear());
    return count;
  }

  // ==================== Query Operations ====================

  /**
   * Get vaults sorted by creation date (newest first)
   */
  async getVaultsSorted(limit?: number): Promise<VaultItem[]> {
    const results = await this.withCursor<VaultItem>(
      'createdAt',
      'prev', // Descending order (newest first)
      (cursor, results) => {
        results.push(cursor.value as VaultItem);
        if (limit && results.length >= limit) {
          return false;
        }
        return true;
      }
    );
    return results;
  }

  /**
   * Search vaults by name (case-insensitive partial match)
   */
  async searchVaultsByName(query: string): Promise<VaultItem[]> {
    if (!query || query.trim().length === 0) {
      return this.getAllVaults();
    }

    const normalizedQuery = query.toLowerCase().trim();
    const allVaults = await this.getAllVaults();

    return allVaults.filter((vault) =>
      vault.name.toLowerCase().includes(normalizedQuery)
    );
  }

  /**
   * Get vaults by hash value
   */
  async getVaultsByHash(hash: string): Promise<VaultItem[]> {
    const db = await this.getDb();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('hash');
      const request = index.getAll(hash);

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(new Error(`Hash query failed: ${request.error?.message || 'Unknown error'}`));
    });
  }

  /**
   * Delete expired vaults (time-locked vaults past their expiration)
   */
  async deleteExpiredVaults(): Promise<number> {
    const now = new Date().toISOString();
    const allVaults = await this.getAllVaults();
    let deletedCount = 0;

    const expiredVaults = allVaults.filter((vault) => {
      return vault.expiresAt && vault.expiresAt < now;
    });

    for (const vault of expiredVaults) {
      await this.deleteVault(vault.id);
      deletedCount++;
    }

    return deletedCount;
  }

  // ==================== Statistics ====================

  /**
   * Get storage statistics
   */
  async getStats(): Promise<StorageStats> {
    const vaults = await this.getVaultsSorted();

    let totalSize = 0;
    let oldestVault: string | null = null;
    let newestVault: string | null = null;

    for (const vault of vaults) {
      // Calculate size based on metadata
      if (vault.metadata?.size) {
        totalSize += vault.metadata.size;
      }
    }

    if (vaults.length > 0) {
      // Sorted newest first, so first is newest, last is oldest
      const newest = vaults[0];
      const oldest = vaults[vaults.length - 1];
      if (newest) newestVault = newest.createdAt;
      if (oldest) oldestVault = oldest.createdAt;
    }

    return {
      totalVaults: vaults.length,
      totalSize,
      oldestVault,
      newestVault,
    };
  }

  // ==================== Export/Import ====================

  /**
   * Export all vaults as JSON for backup
   * Includes version info for future migration compatibility
   */
  async exportVaults(): Promise<string> {
    const vaults = await this.getAllVaults();

    const exportData: ExportData = {
      version: EXPORT_FORMAT_VERSION,
      exportedAt: new Date().toISOString(),
      vaults,
      cryptoParams: {
        pbkdf2Iterations: 600000,
        algorithm: 'AES-GCM',
      },
    };

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Import vaults from JSON backup
   * Skips vaults that already exist (by ID)
   * Handles migration from older export formats
   */
  async importVaults(jsonData: string): Promise<ImportResult> {
    const result: ImportResult = {
      imported: 0,
      skipped: 0,
      errors: [],
    };

    let data: ExportData;

    try {
      data = JSON.parse(jsonData);
    } catch {
      result.errors.push('Invalid JSON format');
      return result;
    }

    if (!data.vaults || !Array.isArray(data.vaults)) {
      result.errors.push('Invalid export format: missing vaults array');
      return result;
    }

    // Check for version compatibility and warn about older formats
    const version = data.version || '1.0.0';
    const majorVersion = parseInt(version.split('.')[0] || '1', 10);

    if (majorVersion < 2) {
      // v1.x exports used 100k PBKDF2 iterations
      // Vaults encrypted with v1.x will still work (iterations stored per-vault)
      // but we log a warning for awareness
      console.warn(
        `Importing from older export format v${version}. ` +
        `Encrypted vaults will use their original PBKDF2 iterations.`
      );
    }

    for (const vault of data.vaults) {
      try {
        // Validate required fields
        if (!vault.id || !vault.name || !vault.hash || !vault.createdAt) {
          result.errors.push(`Invalid vault data: missing required fields`);
          continue;
        }

        // Check if vault already exists
        const exists = await this.vaultExists(vault.id);
        if (exists) {
          result.skipped++;
          continue;
        }

        await this.saveVault(vault);
        result.imported++;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        result.errors.push(`Failed to import vault ${vault.id}: ${message}`);
      }
    }

    return result;
  }

  /**
   * Get the current export format version
   */
  getExportVersion(): string {
    return EXPORT_FORMAT_VERSION;
  }

  // ==================== Utility Methods ====================

  /**
   * Check if a vault exists by ID
   */
  async vaultExists(id: string): Promise<boolean> {
    const vault = await this.getVault(id);
    return vault !== null;
  }

  /**
   * Close the database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      // Reset the promise for potential re-initialization
      this.dbReady = new Promise((resolve, reject) => {
        this.resolveDbReady = resolve;
        this.rejectDbReady = reject;
      });
    }
  }

  /**
   * Delete the entire database (for testing/reset)
   */
  static async deleteDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('IndexedDB is not supported in this environment'));
        return;
      }

      const request = indexedDB.deleteDatabase(DB_NAME);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to delete database: ${request.error?.message || 'Unknown error'}`));
      request.onblocked = () => {
        console.warn('Database deletion blocked - close all connections first');
        resolve(); // Resolve anyway, the deletion will complete when connections close
      };
    });
  }
}

// Export singleton instance
export const storageService = new StorageService();

// ==================== React Hook ====================

export interface UseStorageResult {
  storageService: StorageService;
  isReady: boolean;
  error: Error | null;
}

/**
 * React hook for initializing and using the storage service
 */
export function useStorage(): UseStorageResult {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const initStorage = useCallback(async () => {
    try {
      await storageService.init();
      setIsReady(true);
      setError(null);
    } catch (err) {
      const storageError = err instanceof Error ? err : new Error('Failed to initialize storage');
      setError(storageError);
      setIsReady(false);
    }
  }, []);

  useEffect(() => {
    initStorage();

    // Cleanup on unmount
    return () => {
      // Note: We don't close the DB on unmount since it's a singleton
      // and may be used by other components
    };
  }, [initStorage]);

  return {
    storageService,
    isReady,
    error,
  };
}
